import type { DomainOwner, SourceCategory, UsageKind } from './consumerContractRegistry.types';
import { SHARED_SUMMARY_BUILDER } from './consumerContractRegistry.relationships';

/** Canonical definition of one row emitted by getDesignerSummaryRows (or parallel public-doc mapper). */
export interface SummaryFieldDefinition {
  fieldKey: string;
  subscribedField: string;
  domainOwner: DomainOwner;
  sourceCategory: SourceCategory;
  sourcePath: string;
  outputLabel: string;
  transformationRule: string;
  appearanceCondition: string;
  fallbackBehaviour: string;
}

export const GET_DESIGNER_SUMMARY_ROWS_REF =
  'src/modules/docs/utils/visualDesignerSummary.ts#getDesignerSummaryRows';

/** Artifacts that consume summary-row fields (directly or via parallel mapper). */
export interface SummaryRowConsumerArtifact {
  artifactId: string;
  usage: UsageKind;
  viaBuilder: string;
  metaInputPath: string;
  codeReference: string;
  /** Summary rows this artifact shows. Omit when it shows every row. */
  fieldKeys?: string[];
}

export const SUPPLIER_PDF_SUMMARY_FIELD_KEYS = [
  'window_type_name',
  'variant',
  'glass_type',
  'glass_specification',
  'beading_type',
  'material_name',
  'frame_color_external',
  'frame_color_internal',
  'frame_color_combined',
  'handle_style',
  'handle_color',
  'hinge_placement',
  'window_hinge_type',
  'open_direction',
  'master_leaf',
  'panel_count',
  'stack_direction',
  'traffic_door',
  'sliding_panels',
  'threshold',
  'georgian_bars',
  'trickle_vents',
  'cill_option',
];

export const SUPPLIER_EMAIL_SUMMARY_FIELD_KEYS = [
  'georgian_bars',
  'glass_specification',
  'frame_color_external',
  'frame_color_internal',
  'hinge_placement',
  'open_direction',
  'master_leaf',
  'panel_count',
  'stack_direction',
  'traffic_door',
  'sliding_panels',
  'threshold',
  'window_hinge_type',
  'trickle_vents',
  'cill_option',
];

export const SUPPLIER_BASKET_SUMMARY_FIELD_KEYS = ['hinge_placement', 'open_direction', 'window_hinge_type'];

export const LINE_DESCRIPTION_SUMMARY_FIELD_KEYS = [
  'reference',
  'width_height_size',
  'bay_tangent_width',
  'height_mm',
  'glass_type',
  'variant',
  'beading_type',
  'frame_color_external',
  'frame_color_internal',
  'frame_color_combined',
  'handle_color',
  'handle_style',
  'hinge_placement',
  'open_direction',
  'master_leaf',
  'panel_count',
  'stack_direction',
  'traffic_door',
  'sliding_panels',
  'threshold',
  'window_hinge_type',
  'trickle_vents',
  'cill_option',
];

export const SUMMARY_ROW_CONSUMER_ARTIFACTS: SummaryRowConsumerArtifact[] = [
  {
    artifactId: 'staff_quote_pdf',
    usage: 'exported',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'meta_json',
    codeReference:
      'DocumentDetailPage.tsx#renderTemplate → getDesignerSummaryRows → item.summaryRows → visualDesignerTemplate.ts#buildKvTableFromSummaryRows',
  },
  {
    artifactId: 'public_customer_pdf',
    usage: 'exported',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'sanitizeVisualMeta(meta_json)',
    codeReference:
      'supabase/functions/public-doc/index.ts#summaryRows (edge copy supabase/functions/_shared/visualDesignerSummary.ts) → inline applyVisualDesignerTemplateBlocks kvTableFromSummaryRows',
  },
  {
    artifactId: 'public_document_viewer',
    usage: 'displayed',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'meta_json',
    codeReference: 'src/modules/docs/pages/PublicDocViewerPage.tsx#getDesignerSpecsRowsPublic',
  },
  {
    artifactId: 'document_detail_ui',
    usage: 'displayed',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'meta_json',
    codeReference:
      'DocumentDetailPage.tsx fenestration grid; QuoteVisualDesignerCard.tsx read-only summary table (getDesignerSummaryRows(meta).map)',
  },
  {
    artifactId: 'supplier_pdf',
    usage: 'exported',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'basket item designerSummaryRows',
    codeReference:
      'src/modules/docs/components/SendVisualDesignerToSupplierModal.tsx#visualDesignerLinesToWindowBasketItems → #buildDedicatedSupplierPdfHtml (summaryValueFor)',
    fieldKeys: SUPPLIER_PDF_SUMMARY_FIELD_KEYS,
  },
  {
    artifactId: 'supplier_email_cid',
    usage: 'displayed',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'order item extras.designerSummaryRows',
    codeReference: 'src/types/windowOrder.ts#convertToWindowOrderItem → src/utils/graphService.ts#designerSummaryValue',
    fieldKeys: SUPPLIER_EMAIL_SUMMARY_FIELD_KEYS,
  },
  {
    artifactId: 'supplier_basket_order',
    usage: 'calculated',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'meta_json',
    codeReference: 'src/modules/docs/components/SendVisualDesignerToSupplierModal.tsx#visualDesignerLinesToWindowBasketItems (summaryValue)',
    fieldKeys: SUPPLIER_BASKET_SUMMARY_FIELD_KEYS,
  },
  {
    artifactId: 'final_survey_review',
    usage: 'displayed',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'meta_json',
    codeReference: 'src/modules/docs/utils/surveyDeltaReview.ts#extractVisualDesignerFields',
  },
  {
    artifactId: 'line_description',
    usage: 'exported',
    viaBuilder: SHARED_SUMMARY_BUILDER,
    metaInputPath: 'meta_json',
    codeReference:
      'src/modules/docs/utils/visualDesignerSummary.ts#buildVisualDesignerLineDescription (LINE_DESCRIPTION_ROW_KEYS)',
    fieldKeys: LINE_DESCRIPTION_SUMMARY_FIELD_KEYS,
  },
];

/**
 * Every row/field getDesignerSummaryRows can produce (verified against QuoteVisualDesignerCard.tsx).
 */
export const DESIGNER_SUMMARY_FIELD_DEFINITIONS: SummaryFieldDefinition[] = [
  {
    fieldKey: 'window_type_name',
    subscribedField: 'Window type name',
    domainOwner: 'quote',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.window_type.name',
    outputLabel: 'Window type',
    transformationRule: 'Trimmed string from window_type.name',
    appearanceCondition: 'Row emitted when name is non-empty after trim',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'profile_definition',
    domainOwner: 'canonical_window',
    subscribedField: 'Profile definition',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.window_style.category',
    outputLabel: 'Profile Definition',
    transformationRule: 'formatProfileDefinition(category)',
    appearanceCondition: 'Row emitted when formatted category is non-empty',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'material_name',
    subscribedField: 'Material name',
    domainOwner: 'quote',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.window_material.name',
    outputLabel: 'Material',
    transformationRule: 'Trimmed string',
    appearanceCondition: 'Non-empty material name',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'width_height_size',
    domainOwner: 'canonical_window',
    subscribedField: 'Overall width × height',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.width_mm, meta_json.specs.height_mm',
    outputLabel: 'Size',
    transformationRule: '"{width_mm}mm × {height_mm}mm" joined with × when both present',
    appearanceCondition: 'Non-bay windows only (not isBayLikePayload)',
    fallbackBehaviour: 'Single dimension shown if only one side set; row omitted if both empty',
  },
  {
    fieldKey: 'bay_tangent_width',
    domainOwner: 'canonical_window',
    subscribedField: 'Bay tangent width',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.options.bay_tangent_width_mm; fallback: templatePayload presentation bay geometry',
    outputLabel: 'Tangent width',
    transformationRule:
      'Prefer options.bay_tangent_width_mm; else derive from templatePayload via computeBayGeometry',
    appearanceCondition: 'Bay windows only (isBayLikePayload)',
    fallbackBehaviour: 'Falls back to specs width_mm formatted as mm when tangent is zero',
  },
  {
    fieldKey: 'height_mm',
    domainOwner: 'canonical_window',
    subscribedField: 'Height',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.height_mm',
    outputLabel: 'Height',
    transformationRule: 'Formatted as "{height_mm}mm"',
    appearanceCondition: 'Bay windows only (companion to Tangent width row)',
    fallbackBehaviour: 'Row omitted when height empty',
  },
  {
    fieldKey: 'reference',
    subscribedField: 'Customer reference',
    domainOwner: 'quote',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.reference',
    outputLabel: 'Reference',
    transformationRule: 'Trimmed string',
    appearanceCondition: 'Non-empty reference',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'glass_type',
    domainOwner: 'canonical_window',
    subscribedField: 'Glass type',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.glass_type',
    outputLabel: 'Glass',
    transformationRule: 'Trimmed display string',
    appearanceCondition: 'Non-empty glass_type',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'glass_specification',
    domainOwner: 'canonical_window',
    subscribedField: 'Glass specification',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.glass_specification',
    outputLabel: 'Glass Specification',
    transformationRule:
      'annealed → Annealed, toughened → Toughened, laminated → Laminated, toughened-laminated → Toughened & Laminated, heat-soak → Heat Soak; other values omitted',
    appearanceCondition:
      'Every item when glass_specification set. Supplier only (SUPPLIER_ONLY_ROW_KEYS): the supplier PDF, supplier email and Final Survey review show it; getDesignerSummaryRows leaves it out of quote and customer outputs',
    fallbackBehaviour: 'Row omitted when unset',
  },
  {
    fieldKey: 'variant',
    domainOwner: 'canonical_window',
    subscribedField: 'Profile type / variant',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.variant',
    outputLabel: 'Profile type',
    transformationRule: 'Trimmed variant label',
    appearanceCondition: 'Non-empty variant',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'beading_type',
    domainOwner: 'canonical_window',
    subscribedField: 'Beading type',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.beading_type',
    outputLabel: 'Beading',
    transformationRule: 'Trimmed beading label',
    appearanceCondition: 'Non-empty beading_type',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'frame_color_external',
    domainOwner: 'canonical_window',
    subscribedField: 'External frame colour / finish',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.frame_color_code_external, frame_color_external, frame_color; FRAME_SIDE_COLOUR_OPTIONS preset lookup',
    outputLabel: 'External colour / finish',
    transformationRule:
      'Preset label from frame_color_code_external when not custom; else frame_color_external or shared frame_color',
    appearanceCondition: 'When external or internal side labels exist (split-colour mode)',
    fallbackBehaviour: 'Falls back to sharedLabel on external row when ext empty',
  },
  {
    fieldKey: 'frame_color_internal',
    domainOwner: 'canonical_window',
    subscribedField: 'Internal frame colour / finish',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.frame_color_code_internal, frame_color_internal, frame_color; FRAME_SIDE_COLOUR_OPTIONS preset lookup',
    outputLabel: 'Internal colour / finish',
    transformationRule:
      'Preset label from frame_color_code_internal when not custom; else frame_color_internal or shared frame_color',
    appearanceCondition: 'When external or internal side labels exist (split-colour mode)',
    fallbackBehaviour: 'Falls back to sharedLabel on internal row when int empty',
  },
  {
    fieldKey: 'frame_color_combined',
    domainOwner: 'canonical_window',
    subscribedField: 'Combined frame colour / finish',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.frame_color, frame_color_external, frame_color_internal (merged display)',
    outputLabel: 'Colour / finish',
    transformationRule:
      'sharedLabel: single value when sides match, else "ext (external) / int (internal)"',
    appearanceCondition:
      'Only when neither side label resolves. Side labels fall back to frame_color, so any colour value produces the External/Internal rows instead; in practice this row is never emitted by getDesignerSummaryRows',
    fallbackBehaviour: 'Row omitted when sharedLabel empty',
  },
  {
    fieldKey: 'handle_color',
    domainOwner: 'canonical_window',
    subscribedField: 'Handle colour',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.handle_color',
    outputLabel: 'Handle colour',
    transformationRule: 'Trimmed string',
    appearanceCondition: 'Non-empty handle_color',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'handle_style',
    domainOwner: 'canonical_window',
    subscribedField: 'Handle style',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.handle_style',
    outputLabel: 'Handle style',
    transformationRule: 'Trimmed string',
    appearanceCondition: 'Non-empty handle_style',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'hinge_placement',
    domainOwner: 'canonical_window',
    subscribedField: 'Door hinge placement',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.hinge_placement',
    outputLabel: 'Hinge',
    transformationRule: 'left → Left; any other non-empty value → Right',
    appearanceCondition:
      'Single doors and Door & Window when hinge_placement set (getDoorKind single / door_window). French Doors, Bi-Folds and Sliders do not show it',
    fallbackBehaviour: 'Row omitted for windows, the multi-leaf door types and when unset',
  },
  {
    fieldKey: 'open_direction',
    domainOwner: 'canonical_window',
    subscribedField: 'Door open direction',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.open_direction',
    outputLabel: 'Opens',
    transformationRule: 'in → In, bi-directional → Bi-directional; any other non-empty value → Out',
    appearanceCondition:
      'Door items except Sliders when open_direction set. Door test: isDoorType (upvc-door, french-doors, bifold-doors, sliding-doors, or a type name containing the word "door")',
    fallbackBehaviour: 'Row omitted for windows, Sliders and when unset',
  },
  {
    fieldKey: 'master_leaf',
    domainOwner: 'canonical_window',
    subscribedField: 'French doors master leaf',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.master_leaf',
    outputLabel: 'Master leaf',
    transformationRule: 'left → Left, right → Right (viewed from outside); other values omitted',
    appearanceCondition: 'French Doors (window_type.id french-doors) when master_leaf set',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'panel_count',
    domainOwner: 'canonical_window',
    subscribedField: 'Door panel count',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.panel_count',
    outputLabel: 'Panels',
    transformationRule: 'Rounded whole number',
    appearanceCondition: 'Bi-Folds and Sliders when panel_count is above zero',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'stack_direction',
    domainOwner: 'canonical_window',
    subscribedField: 'Bi-fold stack direction',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.stack_direction',
    outputLabel: 'Stack',
    transformationRule: 'left → Left, right → Right, split → Split (viewed from outside); other values omitted',
    appearanceCondition: 'Bi-Folds when stack_direction set',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'traffic_door',
    domainOwner: 'canonical_window',
    subscribedField: 'Bi-fold traffic door',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.traffic_door',
    outputLabel: 'Traffic door',
    transformationRule: 'none → None, left → Left, right → Right (viewed from outside); other values omitted',
    appearanceCondition: 'Bi-Folds when traffic_door set',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'sliding_panels',
    domainOwner: 'canonical_window',
    subscribedField: 'Sliding panels (from the drawing)',
    sourceCategory: 'canonical_payload',
    sourcePath: 'meta_json.window_style.templatePayload.slideOpens + root',
    outputLabel: 'Sliding',
    transformationRule: 'describeSlidingPanels: schedule refs in drawing order, e.g. "A2 slides left, A3 slides right"',
    appearanceCondition: 'Sliders when at least one panel in the drawing slides',
    fallbackBehaviour: 'Row omitted',
  },
  {
    fieldKey: 'threshold',
    domainOwner: 'canonical_window',
    subscribedField: 'Door threshold',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.threshold',
    outputLabel: 'Threshold?',
    transformationRule:
      'standard → Standard, low-aluminium → Low Aluminium, low-upvc → Low uPVC, mobility-mds25 → Mobility MDS25; other values omitted',
    appearanceCondition: 'Every door type (isDoorType), including Door & Window, when threshold set',
    fallbackBehaviour: 'Row omitted for windows and when unset',
  },
  {
    fieldKey: 'trickle_vents',
    domainOwner: 'canonical_window',
    subscribedField: 'Trickle vents',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.trickle_vents, trickle_vents_position',
    outputLabel: 'Trickle vents',
    transformationRule:
      'true → formatTrickleVentsPositionLabel(position): sash → In Sash, head → In Head, extender → Extender, custom → Custom, anything else → Yes; false → No',
    appearanceCondition: 'When trickle_vents is explicitly true or false',
    fallbackBehaviour: 'Row omitted when trickle_vents undefined',
  },
  {
    fieldKey: 'cill_option',
    domainOwner: 'canonical_window',
    subscribedField: 'Cill option',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.options.cill_option; union placement from cill_union_mode* / templatePayload presentation',
    outputLabel: 'Cill',
    transformationRule:
      '150/180/stub/none → labelled mm or Stub/None; union rectUnion appends placement (Window Only / Door & Window / Door Only)',
    appearanceCondition: 'When cill_option resolves to a non-empty label',
    fallbackBehaviour: 'Row omitted when cill_option unset or unmapped',
  },
  {
    fieldKey: 'extenders_mm',
    domainOwner: 'canonical_window',
    subscribedField: 'Frame extenders',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.ext_top_mm, ext_bottom_mm, ext_left_mm, ext_right_mm',
    outputLabel: 'Extenders (mm)',
    transformationRule: 'Formatted as T{t} B{b} L{l} R{r} when any value non-zero',
    appearanceCondition: 'Any extender mm value > 0',
    fallbackBehaviour: 'Row omitted when all zero',
  },
  {
    fieldKey: 'georgian_bars',
    domainOwner: 'canonical_window',
    subscribedField: 'Georgian bars',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.options.georgian_bars, georgian_cols, georgian_rows, georgian_width_mm',
    outputLabel: 'Georgian',
    transformationRule: 'Grid cols×rows plus bar width mm when georgian_bars === true',
    appearanceCondition: 'georgian_bars === true',
    fallbackBehaviour: 'Row omitted when georgian_bars not true',
  },
  {
    fieldKey: 'astragal_bars',
    domainOwner: 'canonical_window',
    subscribedField: 'Astragal bars',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.options.astragal_bars, astragal_cols, astragal_rows, astragal_width_mm',
    outputLabel: 'Astragal',
    transformationRule: 'Grid cols×rows plus bar width mm when astragal_bars === true',
    appearanceCondition: 'astragal_bars === true',
    fallbackBehaviour: 'Row omitted when astragal_bars not true',
  },
  {
    fieldKey: 'bay_facet_widths',
    domainOwner: 'canonical_window',
    subscribedField: 'Bay facet widths',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.options.bay_facet_widths_mm; fallback templatePayload presentation bayConfig facets',
    outputLabel: 'Facet widths (mm)',
    transformationRule: 'Comma-separated rounded mm values',
    appearanceCondition: 'Bay window with at least one facet width',
    fallbackBehaviour: 'Row omitted for non-bay or empty facet list',
  },
  {
    fieldKey: 'bay_projection_depth',
    domainOwner: 'canonical_window',
    subscribedField: 'Bay projection depth',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.options.bay_projection_depth_mm; fallback curved bow payload geometry',
    outputLabel: 'Projection depth',
    transformationRule: 'Rounded mm suffix for curved bow bays',
    appearanceCondition: 'Curved bow bay with projection depth > 0',
    fallbackBehaviour: 'Row omitted for non-curved bays',
  },
  {
    fieldKey: 'bay_joint_angles',
    domainOwner: 'canonical_window',
    subscribedField: 'Bay joint angles',
    sourceCategory: 'quote_owned',
    sourcePath:
      'meta_json.specs.options.bay_joint_angles_deg; fallback templatePayload joint geometry',
    outputLabel: 'Joint angles (deg)',
    transformationRule: 'Comma-separated rounded degrees with ° suffix',
    appearanceCondition: 'Bay window with at least one joint angle',
    fallbackBehaviour: 'Row omitted for non-bay or empty joint list',
  },
  {
    fieldKey: 'window_hinge_type',
    domainOwner: 'canonical_window',
    subscribedField: 'Window hinge type',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.window_hinge_type_code, window_hinge_type, hinge_type*',
    outputLabel: 'Hinge type',
    transformationRule:
      'Label or code map: egress, ez_clean, fire_rated, standard; strips (default) suffix',
    appearanceCondition: 'Every non-door item (same door test as Hinge); always emitted, even when no hinge type was chosen',
    fallbackBehaviour: 'Shows Standard when no label and the code is missing or unrecognised',
  },
  {
    fieldKey: 'astragal_bar_side',
    domainOwner: 'canonical_window',
    subscribedField: 'Astragal bar side',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.options.astragal_bar_side (fallback astragal_side), astragal_bar_side_label',
    outputLabel: 'Astragal bar side',
    transformationRule: 'Prefer astragal_bar_side_label; else side code when not none/both',
    appearanceCondition: 'Side code not none/both or explicit side label',
    fallbackBehaviour: 'Row omitted when side is none/both/unset',
  },
  {
    fieldKey: 'notes',
    subscribedField: 'Line notes',
    domainOwner: 'quote',
    sourceCategory: 'quote_owned',
    sourcePath: 'meta_json.specs.notes',
    outputLabel: 'Notes',
    transformationRule: 'Trimmed free-text string',
    appearanceCondition: 'Non-empty notes',
    fallbackBehaviour: 'Row omitted when blank',
  },
];

export function summaryFieldCount(): number {
  return DESIGNER_SUMMARY_FIELD_DEFINITIONS.length;
}
